import React from 'react';
import { SetProperty, ObserverData } from '../types';

interface MenuBarProps {
    observerData: ObserverData;
    setProperty: SetProperty;
    onShowAbout: () => void;
}

interface MenuBarState {
    activeMenu: string | null;
}

class MenuBar extends React.Component<MenuBarProps, MenuBarState> {
    private menuRef: React.RefObject<HTMLDivElement>;

    constructor(props: MenuBarProps) {
        super(props);
        this.state = {
            activeMenu: null
        };
        this.menuRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event: MouseEvent) => {
        if (this.menuRef.current && !this.menuRef.current.contains(event.target as Node)) {
            this.setState({ activeMenu: null });
        }
    };

    toggleMenu = (menuName: string) => {
        this.setState(prevState => ({
            activeMenu: prevState.activeMenu === menuName ? null : menuName
        }));
    };

    closeMenu = () => {
        this.setState({ activeMenu: null });
    };

    handleCaptureCurrentView = async () => {
        this.closeMenu();
        if ((window as any).viewer) {
            await (window as any).viewer.captureCurrentViewAsWebP();
        } else {
            console.error('Viewer not available on window object');
        }
    };

    handleToggleCameraInfo = () => {
        this.closeMenu();
        const currentValue = this.props.observerData.ui.cameraInfoVisible === true;
        this.props.setProperty('ui.cameraInfoVisible', !currentValue);
    };

    handleToggleOrbitMode = () => {
        this.closeMenu();
        const currentMode = this.props.observerData.camera.uiCameraMode || 'orbit';
        const newMode = currentMode === 'orbit' ? 'free' : 'orbit';
        this.props.setProperty('camera.uiCameraMode', newMode);
    };

    handleOpenPanel = (panelName: string) => {
        this.closeMenu();
        this.props.setProperty('ui.active', panelName);
    };

    handlePngScreenshot = () => {
        this.closeMenu();
        if ((window as any).viewer) {
            (window as any).viewer.downloadPngScreenshot();
        }
    };

    handleSideViewScreenshot = () => {
        this.closeMenu();
        if ((window as any).viewer) {
            (window as any).viewer.downloadSideViewTransparentAdvanced();
        }
    };

    handleSetupScreenshotAnims = () => {
        this.closeMenu();
        if ((window as any).viewer) {
            (window as any).viewer.applyScreenshotAnimationSettings();
        }
    };

    handleCameraSelect = (camera: any) => {
        console.log('Camera selected from menu:', camera);
        this.closeMenu();
        if ((window as any).viewer) {
            (window as any).viewer.moveCameraToAircraftCamera(camera);
        } else {
            console.error('Viewer not available on window object');
        }
    };

    render() {
        const { activeMenu } = this.state;
        const cameraInfoVisible = this.props.observerData.ui.cameraInfoVisible === true;
        const activePanel = this.props.observerData.ui.active;
        const aircraftCameras = this.props.observerData.aircraftCameras || [];

        return (
            <div className="menu-bar" ref={this.menuRef}>
                <div className="menu-bar-items">
                    {/* File Menu */}
                    <div className="menu-bar-item">
                        <div 
                            className={`menu-bar-label ${activeMenu === 'file' ? 'active' : ''}`}
                            onClick={() => this.toggleMenu('file')}
                        >
                            File
                        </div>
                        {activeMenu === 'file' && (
                            <div className="menu-dropdown">
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={this.handleCaptureCurrentView}
                                >
                                    Save Screenshot...
                                </div>
                                <div 
                                    className="menu-dropdown-item checkbox-item"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const currentValue = this.props.observerData.screenshot.preserveCamera;
                                        this.props.setProperty('screenshot.preserveCamera', !currentValue);
                                    }}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                >
                                    <input 
                                        type="checkbox" 
                                        checked={this.props.observerData.screenshot.preserveCamera}
                                        readOnly
                                        style={{ pointerEvents: 'none' }}
                                    />
                                    <span>Preserve Camera & Rotation</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* View Menu */}
                    <div className="menu-bar-item">
                        <div 
                            className={`menu-bar-label ${activeMenu === 'view' ? 'active' : ''}`}
                            onClick={() => this.toggleMenu('view')}
                        >
                            View
                        </div>
                        {activeMenu === 'view' && (
                            <div className="menu-dropdown">
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={this.handleToggleCameraInfo}
                                >
                                    {cameraInfoVisible ? '☑' : '☐'} Camera Info
                                </div>
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={this.handleToggleOrbitMode}
                                >
                                    Camera: {(this.props.observerData.camera.uiCameraMode || 'orbit') === 'orbit' ? '🎯 Orbit Cam' : '✈️ Free Cam'}
                                </div>
                                <div className="menu-dropdown-divider"></div>
                                {/* Aircraft Cameras - Direct List */}
                                {aircraftCameras.length > 0 && (
                                    <>
                                        <div className="menu-dropdown-label">Cameras</div>
                                        {aircraftCameras.map((camera: any, index: number) => {
                                            const cameraName = camera.cameraName || camera.CameraName || `Camera ${index + 1}`;
                                            return (
                                                <div 
                                                    key={index}
                                                    className="menu-dropdown-item menu-dropdown-camera"
                                                    onClick={() => this.handleCameraSelect(camera)}
                                                >
                                                    {cameraName}
                                                </div>
                                            );
                                        })}
                                        <div className="menu-dropdown-divider"></div>
                                    </>
                                )}
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={() => this.handleOpenPanel('camera')}
                                >
                                    {activePanel === 'camera' ? '☑' : '☐'} Camera Settings
                                </div>
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={() => this.handleOpenPanel('skybox')}
                                >
                                    {activePanel === 'skybox' ? '☑' : '☐'} Sky Settings
                                </div>
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={() => this.handleOpenPanel('light')}
                                >
                                    {activePanel === 'light' ? '☑' : '☐'} Light Settings
                                </div>
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={() => this.handleOpenPanel('debug')}
                                >
                                    {activePanel === 'debug' ? '☑' : '☐'} Debug Settings
                                </div>
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={() => this.handleOpenPanel('aircraft-rotation')}
                                >
                                    {activePanel === 'aircraft-rotation' ? '☑' : '☐'} Aircraft Rotation
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Dev Menu - Hidden */}
                    {/* <div className="menu-bar-item">
                        <div 
                            className={`menu-bar-label ${activeMenu === 'dev' ? 'active' : ''}`}
                            onClick={() => this.toggleMenu('dev')}
                        >
                            Dev
                        </div>
                        {activeMenu === 'dev' && (
                            <div className="menu-dropdown">
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={this.handlePngScreenshot}
                                >
                                    PNG Screenshot
                                </div>
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={this.handleSideViewScreenshot}
                                >
                                    Side View Screenshot
                                </div>
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={this.handleSetupScreenshotAnims}
                                >
                                    Setup Screenshot Animations
                                </div>
                            </div>
                        )}
                    </div> */}

                    {/* Help Menu */}
                    <div className="menu-bar-item">
                        <div 
                            className={`menu-bar-label ${activeMenu === 'help' ? 'active' : ''}`}
                            onClick={() => this.toggleMenu('help')}
                        >
                            Help
                        </div>
                        {activeMenu === 'help' && (
                            <div className="menu-dropdown">
                                <div 
                                    className="menu-dropdown-item"
                                    onClick={() => {
                                        this.closeMenu();
                                        this.props.onShowAbout();
                                    }}
                                >
                                    About
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default MenuBar;

